import type { AppState } from '@store/types'

import {
  buildIdMapByKey,
  normalizeHexColor,
  walkSectionNodes,
  type XmlImportModel,
  type XmlImportNodeItem,
} from '@/utils/xml'

export interface ImportedEntitiesStats {
  expectedSectionCount: number
  expectedNodeCount: number
  expectedBlockCount: number
  expectedTagCount: number
}

export const buildProjectColorMap = (state: AppState): Map<string, string> =>
  buildIdMapByKey({
    items: state.manager.project.colors,
    getKey: (color) => normalizeHexColor(color.hexCode),
    getId: (color) => color.id,
  })

export const countImportedEntities = (
  model: XmlImportModel
): ImportedEntitiesStats => {
  let nodeCount = 0
  let blockCount = 0

  walkSectionNodes(model.sections, (node: XmlImportNodeItem) => {
    nodeCount += 1
    blockCount += node.blocks.length
  })

  return {
    expectedSectionCount: Math.max(model.sections.length, 1),
    expectedNodeCount: nodeCount,
    expectedBlockCount: blockCount,
    expectedTagCount: model.tags.length,
  }
}

export const hasProjectRelations = (state: AppState): boolean => {
  const { dependencies } = state.relations
  const tabIds = state.manager.project.tabs.map((tab) => tab.id)
  const sectionIds = state.manager.project.sections.map((section) => section.id)
  const nodeIds = state.manager.project.nodes.map((node) => node.id)
  const blockIds = Object.keys(state.manager.project.blocks)

  const hasTabs = tabIds.every((id) => dependencies[id]?.type === 'tab')
  const hasSections = sectionIds.every(
    (id) => dependencies[id]?.type === 'section'
  )
  const hasNodes = nodeIds.every((id) => dependencies[id]?.type === 'node')
  const hasBlocks = blockIds.every((id) => dependencies[id]?.type === 'block')

  return hasTabs && hasSections && hasNodes && hasBlocks
}
